import {ChangeDetectionStrategy, Component, EventEmitter, OnInit} from '@angular/core';
import { NbWindowRef, NbInputModule, NbCardModule, NbButtonModule, NbSpinnerModule } from '@nebular/theme';
import {
  ApiResponse,
  BaseComponent,
  CartProduct,
  CustomerGroup,
  HeaderAmount,
  RegionHeaderAmount,
  SpecificationService,
  StoreService
} from 'compob2b-libs';
import { UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {debounceTime, distinctUntilChanged, filter, map, takeUntil, tap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
    templateUrl: './add-to-cart-manually.component.html',
    styleUrls: ['./add-to-cart-manually.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NbInputModule, CdkTextareaAutosize, ReactiveFormsModule, NbCardModule, NbButtonModule, NbSpinnerModule, AsyncPipe]
})
export class AddToCartManuallyComponent extends BaseComponent implements OnInit {
  submitted = false;
  rows$: Observable<RegionHeaderAmount>;
  rows: RegionHeaderAmount;
  onFoundProducts: EventEmitter<CartProduct[]>;
  private region: number | null = null;

  inputFormControl = new UntypedFormControl('', Validators.required);
  readonly customerGroup$ = this.storeService.getCustomerGroup();

  constructor(private readonly windowRef: NbWindowRef,
              private readonly storeService: StoreService,
              private readonly specificationService: SpecificationService) {
    super();
  }

  private static parseText(text: string): HeaderAmount[] {
    const rows = AddToCartManuallyComponent.splitText(text);
    const headerAmount: HeaderAmount[] = [];
    if (rows.length > 0) {
      rows.forEach(row => headerAmount.push(AddToCartManuallyComponent.parseLine(row)));
    }

    return headerAmount;
  }

  private static parseLine(line: string): HeaderAmount {
    line = line.replace(/\s*\r*\n$/g, '');
    const header = AddToCartManuallyComponent.parseHeader(line);
    const amount = AddToCartManuallyComponent.parseAmount(line);

    return {header, amount};
  }

  private static parseHeader(str: string): string {
    str = str.replace(/\s+\d+$/, '');
    return str;
  }

  private static parseAmount(str: string): number {
    const pattern = /(\d+)$/i;
    const match = str.match(pattern);
    return match !== null && match.length ? parseInt(match[0], 10) : 1;
  }

  private static splitText(text: string): string[] {
    const t = text.trim();
    return t.length === 0 ? [] : t.split(/\r*\n/);
  }

  ngOnInit(): void {

    this.customerGroup$.pipe(tap(data => this.handleCustomerGroup(data)), takeUntil(this.unsubscribe)).subscribe();

    this.rows$ = this.inputFormControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(term => term !== null),
        map((term: string) => AddToCartManuallyComponent.parseText(term)),
        map((data: HeaderAmount[]) => this.createRegionHeaderAmount(data)),
        tap((rows: RegionHeaderAmount) => this.rows = rows)
      );
  }

  private createRegionHeaderAmount(data: HeaderAmount[]): RegionHeaderAmount {
    return {
      region: this.region,
      headerAmount: data
    };
  }

  private handleCustomerGroup(customerGroup: CustomerGroup) {
    this.region = customerGroup?.id;
  }

  close() {
    this.windowRef.close();
  }

  onSubmit() {
    this.submitted = true;
    this.specificationService.importManual(this.rows).pipe(
      tap((response: ApiResponse<CartProduct[]>) => this.handleResponse(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  private handleResponse(response: ApiResponse<CartProduct[]>) {
    this.submitted = false;
    this.inputFormControl.reset();
    this.onFoundProducts.next(response.data);
    this.cdr.detectChanges();
    this.close();
  }

}
